import { Link } from "react-router-dom";
import { Search, MessageCircle, Star, ShieldCheck } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";

const IMAGES = {
  hero: "https://images.unsplash.com/photo-1542242476-5a3565835a38?auto=format&fit=crop&w=1800&q=80",
  passengers: "https://images.unsplash.com/photo-1687577937784-1ff9341f401e?auto=format&fit=crop&w=900&q=80",
  parcels: "https://images.pexels.com/photos/4440774/pexels-photo-4440774.jpeg?auto=compress&cs=tinysrgb&w=900",
  drivers: "https://images.unsplash.com/photo-1604599775968-b850e2ceb15e?auto=format&fit=crop&w=900&q=80",
};

const Section = ({ image, title, text, cta, ctaTo, ctaTestid, reverse, accent, imgTestid }) => (
  <section className={`max-w-7xl mx-auto px-6 py-14 md:py-20 grid gap-10 md:grid-cols-2 items-center ${reverse ? "md:[&>*:first-child]:order-2" : ""}`}>
    <div className="overflow-hidden rounded-2xl shadow-[0_12px_40px_rgba(15,41,66,0.12)]">
      <img src={image} alt={title} data-testid={imgTestid} className="w-full h-64 md:h-96 object-cover hover:scale-105 transition-transform duration-500" />
    </div>
    <div className="fade-in">
      <h2 className="text-3xl md:text-4xl font-bold text-primary mb-5">{title}</h2>
      <p className="text-base md:text-lg text-slate-600 mb-8 max-w-lg">{text}</p>
      <Link to={ctaTo}>
        <Button data-testid={ctaTestid} className={`rounded-full px-8 h-12 font-semibold text-white ${accent ? "bg-accent hover:bg-accent/90" : "bg-primary hover:bg-primary/90"}`}>{cta}</Button>
      </Link>
    </div>
  </section>
);

export default function Home() {
  const { t } = useLanguage();
  const steps = [
    { icon: Search, title: t("home.how1Title"), text: t("home.how1Text"), testid: "how-step-1" },
    { icon: MessageCircle, title: t("home.how2Title"), text: t("home.how2Text"), testid: "how-step-2" },
    { icon: Star, title: t("home.how3Title"), text: t("home.how3Text"), testid: "how-step-3" },
  ];
  return (
    <div data-testid="home-page">
      <section className="relative">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMAGES.hero})` }} />
        <div className="absolute inset-0 bg-primary/60" />
        <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-14 md:pt-24 md:pb-20 flex flex-col items-start gap-8 min-h-[70vh] justify-center">
          <div className="max-w-2xl fade-in">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-5 [text-shadow:0_2px_12px_rgba(0,0,0,0.35)]">{t("hero.title")}</h1>
            <p className="text-base md:text-lg text-white/90 [text-shadow:0_1px_8px_rgba(0,0,0,0.4)]">{t("hero.subtitle")}</p>
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5 fade-in">
            <Link to="/pesquisa">
              <Button data-testid="home-search-cta" className="rounded-full px-10 h-14 bg-accent hover:bg-accent/90 text-white font-bold text-lg shadow-lg">{t("search.searchCta")}</Button>
            </Link>
            <div className="flex items-center gap-6">
              <Link to="/publicar-viagem" data-testid="home-publish-link" className="text-white/85 hover:text-white text-sm font-medium underline-offset-4 hover:underline transition-colors duration-200">{t("nav.publishTrip")}</Link>
              <Link to="/painel" data-testid="home-account-link" className="text-white/85 hover:text-white text-sm font-medium underline-offset-4 hover:underline transition-colors duration-200">{t("search.myAccount")}</Link>
            </div>
          </div>
        </div>
      </section>

      <Section image={IMAGES.passengers} title={t("home.ridersTitle")} text={t("home.ridersText")} cta={t("home.ridersCta")} ctaTo="/pesquisa?type=rides" ctaTestid="home-search-rides-cta" imgTestid="home-riders-image" />
      <Section image={IMAGES.parcels} title={t("home.parcelsTitle")} text={t("home.parcelsText")} cta={t("home.parcelsCta")} ctaTo="/enviar-encomenda" ctaTestid="home-send-parcel-cta" imgTestid="home-parcels-image" reverse accent />
      <Section image={IMAGES.drivers} title={t("home.driversTitle")} text={t("home.driversText")} cta={t("home.driversCta")} ctaTo="/publicar-viagem" ctaTestid="home-publish-trip-cta" imgTestid="home-drivers-image" />

      <section className="bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-24">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-12">{t("home.howTitle")}</h2>
          <div className="grid gap-8 md:grid-cols-3">
            {steps.map((s, i) => (
              <div key={i} data-testid={s.testid} className="bg-background rounded-2xl p-8 border border-slate-100 hover:shadow-[0_8px_30px_rgba(15,41,66,0.08)] transition-shadow duration-200">
                <span className="inline-flex bg-accent/10 text-accent p-3.5 rounded-2xl mb-5"><s.icon size={22} /></span>
                <h3 className="text-lg font-bold text-primary mb-2">{s.title}</h3>
                <p className="text-sm text-slate-600">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-16 md:py-24">
        <div className="bg-primary rounded-3xl p-8 md:p-14 flex flex-col md:flex-row items-start md:items-center gap-8 justify-between">
          <div className="max-w-xl">
            <div className="flex items-center gap-3 mb-4">
              <ShieldCheck className="text-accent" size={28} />
              <h2 className="text-2xl md:text-3xl font-bold text-white">{t("home.trustTitle")}</h2>
            </div>
            <p className="text-white/80 text-sm md:text-base">{t("home.trustText")}</p>
            <div className="flex flex-wrap gap-2 mt-5">
              {["Pontual", "Simpático", "Condução segura", "Boa comunicação", "Cuidado com a encomenda"].map((tag) => (
                <span key={tag} className="bg-white/10 text-white text-xs font-medium px-3 py-1.5 rounded-full">{tag}</span>
              ))}
            </div>
          </div>
          <Link to="/registar">
            <Button data-testid="home-join-cta" className="rounded-full px-8 h-12 bg-accent hover:bg-accent/90 text-white font-semibold shrink-0">{t("nav.register")}</Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
